import React from 'react';
import { Button, Box, Stack, Typography } from '@mui/material';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useMutation } from 'react-fetching-library';
import { DebounceInput } from 'react-debounce-input';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

import { SmallFormTextField } from '../../../../../components/small-fields/SmallFormTextField';
import { AlertDialogContainer } from '../../../../../components/others/AlertDialogContainer';
import { submitParticipantsAction } from '../../../../../services/request';
import { SmallFormDateField } from '../../../../../components/small-fields/SmallFormDateField';
import { formatDate } from '../../../../../services/functions';
import { ConfirmDialog } from '../../../../../components/ConfirmDialog';

export const ParticipantForm = props => {
  const { data, submitCallback, handleClose } = props;

  const { loading, mutate } = useMutation(submitParticipantsAction);
  const [showAlert, setShowAlert] = React.useState(false);
  const [severity, setSeverity] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);

  const validationSchema = Yup.object({
    name: Yup.string().required('To pole nie może być puste'),
    street: Yup.string().max(200, 'Ulica może mięc najwyżej 200 znaków'),
    postal_code: Yup.string().max(20, 'Kod pocztowy może mięc najwyżej 20 znaków'),
    city: Yup.string().max(50, 'Miasto może mięc najwyżej 50 znaków'),
    pesel: Yup.string().max(11, 'Miasto może mięc najwyżej 11 znaków'),
    phone: Yup.string().max(12, 'Numer telefonu może mięc najwyżej 12 znaków'),
    email: Yup.string().email(),
    birth_date: Yup.date().nullable()
  });

  const initialValues = {
    name: data ? data.name || '' : '',
    street: data ? data.street_address || '' : '',
    postal_code: data ? data.postal_code || '' : '',
    city: data ? data.city || '' : '',
    pesel: data ? data.pesel || '' : '',
    phone: data ? data.phone || '' : '',
    birth_date: data ? data.birth_date || null : null,
    gender: data ? data.gender || 'boy' : 'boy'
  };

  const handleSubmit = async values => {
    const { error, payload } = await mutate(values, data ? 'PUT' : 'POST', data ? data.id : undefined);
    if (!error) {
      submitCallback(payload, data ? 'edit' : 'created', 'success', data ? 'Zaktualizowano dane uczestnika' : 'Dodano nowego uczestnika', '');
      handleClose();
    } else {
      setSeverity('error');
      setTitle('Nie udało się dodać nowego uczestnika');
      setMessage(payload);
      setShowAlert(true);
    }
  };

  const handleDelete = async () => {
    const { error, payload } = await mutate([], 'DELETE', data.id);
    if (!error) {
      submitCallback(data, 'delete', 'success', `Usunięto uczestnika ${data.name}`, '');
      handleClose();
    } else {
      setSeverity('error');
      setTitle(`Nie udało się usunąć uczestnika ${data.name}`);
      setMessage(payload);
      setShowAlert(true);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: values => {
      handleSubmit(values);
    }
  });

  return (
    <>
      {showAlert && (
        <AlertDialogContainer
          severity={severity}
          title={title}
          message={message}
          setShowAlert={setShowAlert}
        />
      )}
      <form onSubmit={formik.handleSubmit} style={{ textAlign: 'center' }}>
        <Box display="flex" flexDirection="row" mt={1}>
          <Stack spacing={1} flex="1 1 50%">
            <Stack direction="row" alignItems="center" justifyContent="right" spacing={1}>
              <Typography variant="subtitle2">Imię i nazwisko</Typography>
              <DebounceInput
                name="name"
                id="name"
                type="text"
                placeholder="imię i nazwisko"
                value={formik.values.name}
                onChange={formik.handleChange}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                debounceTimeout={100}
                element={SmallFormTextField}
                autoFocus
              />
            </Stack>
            <Stack direction="row" alignItems="center" justifyContent="right" spacing={1}>
              <Typography variant="subtitle2">Ulica</Typography>
              <DebounceInput
                name="street"
                id="street"
                type="text"
                placeholder="ulica"
                value={formik.values.street}
                onChange={formik.handleChange}
                error={formik.touched.street && Boolean(formik.errors.street)}
                helperText={formik.touched.street && formik.errors.street}
                debounceTimeout={100}
                element={SmallFormTextField}
              />
            </Stack>
            <Stack direction="row" alignItems="center" justifyContent="right" spacing={1}>
              <Typography variant="subtitle2">Kod pocztowy</Typography>
              <DebounceInput
                name="postal_code"
                id="postal_code"
                type="text"
                placeholder="kod pocztowy"
                value={formik.values.postal_code}
                onChange={formik.handleChange}
                error={formik.touched.postal_code && Boolean(formik.errors.postal_code)}
                helperText={formik.touched.postal_code && formik.errors.postal_code}
                debounceTimeout={100}
                element={SmallFormTextField}
              />
            </Stack>
            <Stack direction="row" alignItems="center" justifyContent="right" spacing={1}>
              <Typography variant="subtitle2">Miejscowość</Typography>
              <DebounceInput
                name="city"
                id="city"
                type="text"
                placeholder="miasto"
                value={formik.values.city}
                onChange={formik.handleChange}
                error={formik.touched.city && Boolean(formik.errors.city)}
                helperText={formik.touched.city && formik.errors.city}
                debounceTimeout={100}
                element={SmallFormTextField}
              />
            </Stack>
          </Stack>

          <Stack spacing={1} flex="1 1 50%" mr="15%">
            <Stack direction="row" alignItems="center" justifyContent="right" spacing={1}>
              <Typography variant="subtitle2">NIP/PESEL</Typography>
              <DebounceInput
                name="pesel"
                id="pesel"
                type="text"
                placeholder="pesel"
                value={formik.values.pesel}
                onChange={formik.handleChange}
                error={formik.touched.pesel && Boolean(formik.errors.pesel)}
                helperText={formik.touched.pesel && formik.errors.pesel}
                debounceTimeout={100}
                element={SmallFormTextField}
              />
            </Stack>
            <Stack direction="row" alignItems="center" justifyContent="right" spacing={1}>
              <Typography variant="subtitle2">Telefon</Typography>
              <DebounceInput
                name="phone"
                id="phone"
                type="text"
                placeholder="phone"
                value={formik.values.phone}
                onChange={formik.handleChange}
                error={formik.touched.phone && Boolean(formik.errors.phone)}
                helperText={formik.touched.phone && formik.errors.phone}
                debounceTimeout={100}
                element={SmallFormTextField}
              />
            </Stack>
            <Stack direction="row" alignItems="center" justifyContent="right" spacing={1}>
              <Typography variant="subtitle2">Data urodzenia</Typography>
              <SmallFormDateField
                name="birth_date"
                id="birth_date"
                type="text"
                placeholder="birth_date"
                value={formik.values.birth_date}
                onChange={val => formik.setFieldValue('birth_date', formatDate(val))}
                error={formik.touched.birth_date && Boolean(formik.errors.birth_date)}
                helperText={formik.touched.birth_date && formik.errors.birth_date}
              />
            </Stack>
            <Stack direction="row" alignItems="center" justifyContent="right" spacing={1}>
              <Typography variant="subtitle2">Płeć</Typography>
              <SmallFormTextField
                select
                id="gender"
                name="gender"
                options={[{ value: 'boy', label: 'Chłopiec' }, { value: 'girl', label: 'Dziewczynka' }]}
                value={formik.values.gender}
                onChange={formik.handleChange} />

            </Stack>
          </Stack>
        </Box>
        <Button
          color="primary"
          variant="contained"
          type="submit"
          sx={{ mt: 3 }}
          disabled={loading}
        >
          <AddIcon />
          Zapisz
        </Button>
        { data
          ? <Button
            color="error"
            variant="contained"
            sx={{ mt: 3, ml: 4 }}
            onClick={() => {
              setConfirmDeleteOpen(true);
            }}
          >
            <DeleteIcon />
          Usuń
          </Button>
          : null}
      </form>
      <ConfirmDialog
        title="Usunąć Uczestnika?"
        open={confirmDeleteOpen}
        setOpen={setConfirmDeleteOpen}
        onConfirm={handleDelete}
        disabled={loading}
      >
        Czy na pewno chcesz usunąć tego uczestnika?
      </ConfirmDialog>
    </>

  );
};
